import React from 'react';
import { motion } from 'framer-motion';
import { BoxIcon, HistoryIcon, MoreVerticalIcon, MoveIcon, Trash2Icon } from 'lucide-react';
import { MenuButton } from '../ui/MenuButton';
import { OrientIcon, ProtectIcon, SupportIcon } from '../icons/ToolIcons';
import { timeLabel } from '../../utils/historyText';
import type { HistoryEntry, HistoryKind } from '../../types/history';
function KindIcon({
  kind
}: {
  kind: HistoryKind;
}) {
  const cls = 'h-[15px] w-[15px] text-pf-ink';
  switch (kind) {
    case 'import':
      return <BoxIcon className={cls} strokeWidth={2.2} data-id="element-185" data-mp="25lfx4hd2ssrr" />;
    case 'orient':
      return <OrientIcon className={cls} data-id="element-186" data-mp="1rnr8cyfw11hf" />;
    case 'supports':
    case 'manual':
      return <SupportIcon className={cls} data-id="element-187" data-mp="1wlsvpvkcjmyp" />;
    case 'clear':
      return <Trash2Icon className={cls} strokeWidth={2.2} data-id="element-188" data-mp="1wo5karkm0vkq" />;
    case 'protect':
      return <ProtectIcon className={cls} data-id="element-189" data-mp="g682eyaxakf6" />;
    case 'move':
      return <MoveIcon className={cls} strokeWidth={2.2} data-id="element-190" data-mp="l7fayzkw3nmw" />;
    case 'restore':
      return <HistoryIcon className={cls} strokeWidth={2.2} data-id="element-191" data-mp="3ble102jyb9hu" />;
  }
}
type HistoryEntryRowProps = {
  entry: HistoryEntry;
  selected: boolean;
  current: boolean;
  today: boolean;
  isFirst: boolean;
  isLast: boolean;
  onSelect: () => void;
  onRestore: () => void;
  onSaveVersion: () => void;
};
export function HistoryEntryRow({
  entry,
  selected,
  current,
  today,
  isFirst,
  isLast,
  onSelect,
  onRestore,
  onSaveVersion
}: HistoryEntryRowProps) {
  const lineColor = today ? 'bg-pf-blue/40' : 'bg-[#c7cbd0]';
  return <motion.li layout="position" initial={{
    opacity: 0,
    y: -6
  }} animate={{
    opacity: 1,
    y: 0
  }} exit={{
    opacity: 0
  }} transition={{
    duration: 0.2,
    ease: [0.23, 1, 0.32, 1]
  }} className="relative pl-[20px]" data-id="element-192" data-mp="3plym55dc9hei">
      
      <span className={`absolute left-[3px] w-px ${lineColor} ${isFirst ? 'top-[20px]' : 'top-0'} ${isLast ? 'h-[20px]' : 'bottom-0'}`} aria-hidden="true" data-id="element-193" data-mp="36m62m49i2wr" />
      <span className={`absolute left-0 top-[17px] h-[7px] w-[7px] rounded-full ${today ? 'bg-pf-blue' : 'bg-[#8d9298]'}`} aria-hidden="true" data-id="element-194" data-mp="d4tc49x0j0jz" />
      <div className={`flex items-center gap-[4px] rounded-[8px] pr-[4px] transition-colors duration-150 ${selected ? 'bg-pf-blue-soft' : 'hover:bg-pf-panel'} ${!isLast && !selected ? 'border-b border-pf-line/70' : 'border-b border-transparent'}`} data-id="element-195" data-mp="k91e5rj97blo">
        
        <button type="button" onClick={onSelect} aria-pressed={selected} className="flex min-w-0 flex-1 items-start gap-[10px] py-[9px] pl-[8px] text-left" data-id="element-196" data-mp="yxntht8vs3cw">
          <span className="flex w-[30px] shrink-0 flex-col items-center gap-[7px] pt-[1px]" data-id="element-197" data-mp="17w9l0dwbyspt">
            <span className="text-[10.5px] tabular-nums text-pf-muted" data-id="element-198" data-mp="1cw5hls2p7hvc">{timeLabel(entry.time)}</span>
            <KindIcon kind={entry.kind} data-id="element-199" data-mp="1hvicn03q3v90" />
          </span>
          <span className="min-w-0 flex-1 pt-[1px]" data-id="element-200" data-mp="1hsw693skjlsd">
            <span className="flex items-center gap-[6px]" data-id="element-201" data-mp="3eo0olue0ey78">
              <span className="truncate text-[12px] font-semibold text-pf-ink" data-id="element-202" data-mp="3eplj5k0bqh53">{entry.title}</span>
              {current && <span className="shrink-0 rounded-full bg-pf-blue px-[6px] py-[1px] text-[9px] font-bold text-white" data-id="element-203" data-mp="20r7hwela5gz5">Current</span>}
            </span>
            <span className="mt-[3px] line-clamp-2 block text-[11px] leading-snug text-pf-muted" data-id="element-204" data-mp="1vzpfdm3lz8c2">{entry.description}</span>
          </span>
          {entry.thumbnail ? <img src={entry.thumbnail} alt={`Model after: ${entry.title}`} className="h-[47px] w-[64px] shrink-0 rounded-[5px] border border-pf-line object-cover" data-id="element-205" data-mp="1n0cc5b62vfzs" /> : <span className="h-[47px] w-[64px] shrink-0 rounded-[5px] border border-pf-line bg-[#eceef1]" data-id="element-206" data-mp="2jnqu5crdrkzx" />}
        </button>
        <MenuButton label={`Actions for ${entry.title}`} icon={<MoreVerticalIcon className="h-[14px] w-[14px]" strokeWidth={2.4} data-id="element-208" data-mp="25qamjxxcll6l" />} items={[{
        label: 'Restore to this step…',
        onSelect: onRestore,
        disabled: current
      }, {
        label: 'Save as version',
        onSelect: onSaveVersion
      }]} data-id="element-207" data-mp="2aovjnohazoqt" />
        
      </div>
    </motion.li>;
}